/**
 * Project cash flow: what moved each month, and where the project stands.
 *
 * The monthly panel is trading - money in, money out, net. The cumulative panel is position, and
 * it carries the one-off events (capital raised, cash distributed) because those are step changes
 * in the project's cash position, not monthly trading. Each panel has a single Y axis, so every
 * series on a chart is read against the same reference.
 */
import React, { useEffect, useState } from 'react';
import {
  ComposedChart, Area, Bar, Line, Scatter, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { api } from '../../../context/AuthContext';
import { money as formatMoney, compactMoney, monthLabel } from './financeFormat';

const money = (value, currency = 'EUR') => formatMoney(value, { currency });
const compact = (value, currency = 'EUR') => compactMoney(value, currency);

const BLUE = 'rgb(37,99,235)';
const PINK = 'rgb(215,57,134)';
const GREEN = 'rgb(16,185,129)';
const RED = 'rgb(244,63,94)';
const INK = 'rgb(15,23,42)';

/** An event marker: an outlined circle carrying its initial, so it reads without the legend. */
const eventMarker = (colour, letter) => ({ cx, cy }) => {
  if (cx === null || cy === null || cx === undefined || cy === undefined) return null;
  return (
    <g>
      <circle cx={cx} cy={cy} r={8} fill="white" stroke={colour} strokeWidth={2} />
      <text x={cx} y={cy + 3.5} textAnchor="middle" fontSize={9} fontWeight={700} fill={colour}>
        {letter}
      </text>
    </g>
  );
};

const CashKey = ({ items }) => (
  <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-foreground-muted mt-3">
    {items.map((item) => (
      <span key={item.label} className="inline-flex items-center gap-1.5">
        {item.shape === 'ring' ? (
          <span className="h-2.5 w-2.5 rounded-full border-2" style={{ borderColor: item.colour }} />
        ) : (
          <span className="h-2.5 w-2.5 rounded-sm" style={{ background: item.colour }} />
        )}
        {item.label}
      </span>
    ))}
  </div>
);

const MonthlyTooltip = ({ active, payload, currency }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="rounded-xl border border-black/[0.08] bg-white px-3.5 py-2.5 shadow-lg text-[13px] space-y-1">
      <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground-muted">
        {monthLabel(row.month)}
      </p>
      <p className="text-foreground-muted">
        Cash in <span className="tabular-nums text-emerald-700">{money(row.inflows, currency)}</span>
      </p>
      <p className="text-foreground-muted">
        Cash out <span className="tabular-nums text-rose-600">{money(row.outflows, currency)}</span>
      </p>
      <p className="text-foreground-muted">
        Net <span className="tabular-nums text-foreground">{money(row.net, currency)}</span>
      </p>
    </div>
  );
};

const CumulativeTooltip = ({ active, payload, currency }) => {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="rounded-xl border border-black/[0.08] bg-white px-3.5 py-2.5 shadow-lg text-[13px] space-y-1">
      <p className="text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground-muted">
        {monthLabel(row.month)}
      </p>
      <p className="text-foreground-muted">
        In to date <span className="tabular-nums text-emerald-700">{money(row.cumIn, currency)}</span>
      </p>
      <p className="text-foreground-muted">
        Out to date <span className="tabular-nums text-rose-600">{money(row.cumOut, currency)}</span>
      </p>
      <p className="text-foreground-muted">
        Position <span className="tabular-nums text-foreground">{money(row.position, currency)}</span>
      </p>
      {row.raise > 0 && <p className="text-primary">Raise {money(row.raise, currency)}</p>}
      {row.distribution > 0 && <p className="text-blue-600">Distribution {money(row.distribution, currency)}</p>}
    </div>
  );
};

export const ProjectCashChart = ({ projectId, currency = 'EUR' }) => {
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/admin/v2/finance/projects/${projectId}/cash-timeline`)
      .then((res) => { if (alive) setData(res.data); })
      .catch(() => { if (alive) setData({ series: [], events: [], totals: {} }); });
    return () => { alive = false; };
  }, [projectId]);

  if (!data) {
    return <div className="h-[320px] flex items-center justify-center text-sm text-foreground-muted">Loading cash flow...</div>;
  }
  if (!data.series?.length) {
    return (
      <p className="py-10 text-center text-sm text-foreground-muted" data-testid="cash-chart-empty">
        No approved money has moved yet, so there is nothing to plot.
      </p>
    );
  }

  const totals = data.totals || {};

  let inSoFar = 0;
  let outSoFar = 0;
  const cumulative = data.series.map((m) => {
    inSoFar += m.inflows;
    outSoFar += m.outflows;
    const events = data.events.filter((e) => e.month === m.month);
    const raise = events.filter((e) => e.type === 'raise').reduce((sum, e) => sum + e.amount, 0);
    const distribution = events.filter((e) => e.type === 'distribution').reduce((sum, e) => sum + e.amount, 0);
    return {
      month: m.month,
      cumIn: inSoFar,
      cumOut: outSoFar,
      position: inSoFar - outSoFar,
      // null keeps months without an event off the event series instead of pinning them to zero
      raise: raise || null,
      distribution: distribution || null,
    };
  });

  const hasEvents = cumulative.some((r) => r.raise || r.distribution);

  return (
    <div data-testid="project-cash-chart">
      <div className="flex flex-wrap gap-x-8 gap-y-3 mt-5">
        {[
          ['Cash out', money(totals.outflows, currency), 'text-foreground'],
          ['Cash in', money(totals.inflows, currency), 'text-emerald-700'],
          ['Raised', money(totals.raised, currency), 'text-primary'],
          ['Distributed', money(totals.distributed, currency), 'text-blue-600'],
        ].map(([label, value, tone]) => (
          <div key={label}>
            <p className="text-[10px] uppercase tracking-[0.14em] text-foreground-muted">{label}</p>
            <p className={`text-base font-semibold tabular-nums mt-0.5 ${tone}`}>{value}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-5 items-stretch">
        <div className="min-w-0" data-testid="cash-chart-monthly">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground-muted mb-2">Monthly</p>
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={data.series} margin={{ top: 16, right: 20, left: 4, bottom: 4 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" vertical={false} />
              <XAxis
                dataKey="month" tickFormatter={monthLabel} tick={{ fontSize: 11 }}
                stroke="rgba(0,0,0,0.3)" tickLine={false}
              />
              <YAxis
                tickFormatter={(v) => compact(v, currency)} tick={{ fontSize: 11 }}
                stroke="rgba(0,0,0,0.3)" width={62} tickLine={false} axisLine={false}
              />
              <Tooltip content={<MonthlyTooltip currency={currency} />} cursor={{ fill: 'rgba(0,0,0,0.03)' }} />
              <ReferenceLine y={0} stroke="rgba(0,0,0,0.25)" />
              <Bar dataKey="inflows" name="Cash in" fill={GREEN} fillOpacity={0.85} radius={[3, 3, 0, 0]} barSize={14} />
              <Bar dataKey="outflows" name="Cash out" fill={RED} fillOpacity={0.85} radius={[3, 3, 0, 0]} barSize={14} />
              <Line
                type="monotone" dataKey="net" name="Net" stroke="rgba(15,23,42,0.5)"
                strokeWidth={1.5} strokeDasharray="4 3" dot={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
          <CashKey
            items={[
              { label: 'Cash in', colour: GREEN, shape: 'square' },
              { label: 'Cash out', colour: RED, shape: 'square' },
              { label: 'Net', colour: 'rgba(15,23,42,0.5)', shape: 'square' },
            ]}
          />
        </div>

        <div className="min-w-0" data-testid="cash-chart-cumulative">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-foreground-muted mb-2">Cumulative</p>
          <ResponsiveContainer width="100%" height={320}>
            <ComposedChart data={cumulative} margin={{ top: 24, right: 24, left: 4, bottom: 4 }}>
              <defs>
                <linearGradient id="cumPosition" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={INK} stopOpacity={0.14} />
                  <stop offset="100%" stopColor={INK} stopOpacity={0.01} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" vertical={false} />
              <XAxis
                dataKey="month" tickFormatter={monthLabel} tick={{ fontSize: 11 }}
                stroke="rgba(0,0,0,0.3)" tickLine={false}
              />
              {/* One axis only: the events are read against the same scale as the cash position */}
              <YAxis
                tickFormatter={(v) => compact(v, currency)} tick={{ fontSize: 11 }}
                stroke="rgba(0,0,0,0.3)" width={62} tickLine={false} axisLine={false}
              />
              <Tooltip content={<CumulativeTooltip currency={currency} />} />
              <ReferenceLine y={0} stroke="rgba(0,0,0,0.25)" />
              {cumulative.filter((r) => r.raise || r.distribution).map((r) => (
                <ReferenceLine
                  key={`mark-${r.month}`} x={r.month}
                  stroke={r.raise ? 'rgba(215,57,134,0.3)' : 'rgba(37,99,235,0.3)'}
                  strokeDasharray="3 3"
                />
              ))}
              <Area
                type="monotone" dataKey="position" name="Cash position (in − out)"
                stroke={INK} strokeWidth={2} fill="url(#cumPosition)"
              />
              <Line type="monotone" dataKey="cumIn" name="Cash in, cumulative" stroke={GREEN} strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="cumOut" name="Cash out, cumulative" stroke={RED} strokeWidth={2} dot={false} />
              <Scatter dataKey="raise" name="Raise" fill={PINK} shape={eventMarker(PINK, 'R')} />
              <Scatter dataKey="distribution" name="Distribution" fill={BLUE} shape={eventMarker(BLUE, 'D')} />
            </ComposedChart>
          </ResponsiveContainer>
          <CashKey
            items={[
              { label: 'Cash in, cumulative', colour: GREEN, shape: 'square' },
              { label: 'Cash out, cumulative', colour: RED, shape: 'square' },
              { label: 'Cash position', colour: INK, shape: 'square' },
              ...(hasEvents
                ? [
                  { label: 'Raise', colour: PINK, shape: 'ring' },
                  { label: 'Distribution', colour: BLUE, shape: 'ring' },
                ]
                : []),
            ]}
          />
        </div>
      </div>
    </div>
  );
};

export default ProjectCashChart;
